import { Descriptions, Divider, Form, Input } from 'antd';

import React from 'react';
import { useTranslation } from 'react-i18next';
import { TipoDatiSoggettiEnte } from 'server/schema/integrations/pdnd/schema/schemas';
import { getResidenceTypeDescription } from './detail/getResidenceTypeDescription';
import { getCitizenAddress } from './detail/getCitizenAddress';
import { getCitizenConsolate } from './detail/getCitizenConsolate';
import { getCitizenForeignAddress } from './detail/getCitizenForeignAddress';
import { getCitizenAddressCity } from './detail/getCitizenAddressCity';
import { getCitizenForeignAddressCity } from './detail/getCitizenForeignAddressCity';
import { VerticalSpace } from 'client/ui/space/VerticalSpace';
import moment from 'moment';

interface IPDNDProfileViewProps {
  citizen?: TipoDatiSoggettiEnte;
}

export function PDNDProfileView(props: IPDNDProfileViewProps) {
  const { citizen } = props;
  if (!citizen) return null;

  const citizenDetails = [
    { label: 'Nome', value: citizen.generalita?.nome },
    { label: 'Cognome', value: citizen.generalita?.cognome },
    {
      label: 'Note (Nome e Cognome)',
      value:
        citizen.generalita?.senzaNome == '1'
          ? 'Senza nome'
          : citizen.generalita?.senzaCognome == '1'
          ? 'Senza cognome'
          : undefined
    },
    {
      label: 'Codice Fiscale',
      value: citizen.generalita?.codiceFiscale?.codFiscale
    },
    {
      label: 'Codice Fiscale (Data Attribuzione Validità)',
      value: citizen.generalita?.codiceFiscale?.dataAttribuzioneValidita
    },
    {
      label: 'Data di Nascita',
      value:
        citizen.generalita?.senzaGiorno == '1'
          ? moment(citizen.generalita?.dataNascita, 'YYYY-MM-DD').format(
              'YYYY-MM'
            )
          : citizen.generalita?.senzaGiornoMese == '1'
          ? moment(citizen.generalita?.dataNascita, 'YYYY-MM-DD').format('YYYY')
          : moment(citizen.generalita?.dataNascita, 'YYYY-MM-DD').format(
              'YYYY-MM-DD'
            )
    },
    {
      label: 'Data di Nascita (Eccezioni)',
      value:
        citizen.generalita?.senzaGiorno == '1'
          ? 'Senza giorno'
          : citizen.generalita?.senzaGiornoMese
          ? 'Senza mese e giorno'
          : undefined
    },
    { label: 'Sesso', value: citizen.generalita?.sesso },
    {
      label: 'ID Scheda Soggetto ANPR',
      value: citizen.generalita?.idSchedaSoggettoANPR
    },
    {
      label: 'Stato di Nascita (Località)',
      value: citizen.generalita?.luogoNascita?.localita?.descrizioneLocalita
    },
    {
      label: 'Stato di Nascita (Descrizione Stato)',
      value: citizen.generalita?.luogoNascita?.localita?.descrizioneStato
    },
    {
      label: 'Stato di Nascita (Codice Stato)',
      value: citizen.generalita?.luogoNascita?.localita?.codiceStato
    },
    {
      label: 'Stato di Nascita (Provincia/Contea)',
      value: citizen.generalita?.luogoNascita?.localita?.provinciaContea
    },
    {
      label: 'Comune di Nascita',
      value: citizen.generalita?.luogoNascita?.comune?.nomeComune
    },
    {
      label: 'Comune di Nascita (Codice Istat)',
      value: citizen.generalita?.luogoNascita?.comune?.codiceIstat
    },
    {
      label: 'Comune di Nascita (Sigla Provincia)',
      value: citizen.generalita?.luogoNascita?.comune?.siglaProvinciaIstat
    },
    {
      label: 'Comune di Nascita (Località)',
      value: citizen.generalita?.luogoNascita?.comune?.descrizioneLocalita
    },
    {
      label: 'Luogo Eccezionale di Nascita',
      value: citizen.generalita?.luogoNascita?.luogoEccezionale
    }
  ];

  const citizenIdentifiers = [
    { label: 'ID ANPR', value: citizen.identificativi?.idANPR }
  ];

  const citizenInfo = citizen.infoSoggettoEnte?.map((info, index) => [
    { label: 'Chiave', value: info.chiave },
    { label: 'Dettaglio', value: info.dettaglio },
    { label: 'ID', value: info.id },
    { label: 'Tipo Valore', value: info.valore },
    { label: 'Valore (data)', value: info.valoreData },
    { label: 'Valore (testo)', value: info.valoreTesto }
  ]);

  const citizenResidence = citizen.residenza?.map((residenza, index) => [
    {
      label: 'Tipo Indirizzo',
      value: getResidenceTypeDescription(Number(residenza.tipoIndirizzo))
    },
    { label: 'Data Decorrenza', value: residenza.dataDecorrenzaResidenza },
    { label: 'Indirizzo', value: getCitizenAddress(residenza.indirizzo) },
    { label: 'Comune', value: getCitizenAddressCity(residenza.indirizzo) },
    {
      label: 'Comune (Codice Istat)',
      value: residenza.indirizzo?.comune?.codiceIstat
    },
    { label: 'CAP', value: residenza.indirizzo?.cap },
    {
      label: 'Località Estera (Consolato)',
      value: getCitizenConsolate(residenza.localitaEstera)
    },
    {
      label: 'Località Estera (Indirizzo)',
      value: getCitizenForeignAddress(residenza.localitaEstera)
    },
    {
      label: 'Località Estera (Comune)',
      value: getCitizenForeignAddressCity(residenza.localitaEstera)
    },
    {
      label: 'Località Estera (Codice Stato)',
      value: residenza.localitaEstera?.indirizzoEstero?.localita?.codiceStato
    },
    { label: 'Presso', value: residenza.presso },
    { label: 'Note', value: residenza.noteIndirizzo }
  ]);

  return (
    <div>
      <Descriptions title="Generalità" bordered column={1} size="small">
        {citizenDetails
          .filter(item => item.value)
          .map((item, index) => (
            <Descriptions.Item label={item.label} key={index}>
              {item.value}
            </Descriptions.Item>
          ))}
      </Descriptions>

      <VerticalSpace />

      <Descriptions title="Identificativi" bordered column={1} size="small">
        {citizenIdentifiers
          .filter(item => item.value)
          .map((item, index) => (
            <Descriptions.Item label={item.label} key={index}>
              {item.value}
            </Descriptions.Item>
          ))}
      </Descriptions>

      <VerticalSpace />

      <Descriptions title="Residenza" bordered column={1} size="small">
        {citizenResidence?.map((residenza, index) =>
          residenza
            .filter(item => item.value)
            .map((item, index) => (
              <Descriptions.Item label={item.label} key={index}>
                {item.value}
              </Descriptions.Item>
            ))
        )}
      </Descriptions>

      <VerticalSpace />

      <Descriptions title="Informazioni Ente" bordered column={1} size="small">
        {citizenInfo?.map((info, infoIndex) =>
          info
            .filter(item => item.value)
            .map((item, index) => (
              <Descriptions.Item label={item.label} key={index}>
                {item.value}
              </Descriptions.Item>
            ))
        )}
      </Descriptions>
    </div>
  );
}
